import React from 'react'
import { Alert, Button, Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import ImpersonationExitButton from './components/ImpersonationExitButton'

// firebase-js-sdk @firebase/firestore occasionally throws this during
// listener registration when two snapshot subscriptions race on the same
// query target. A full page reload clears the SDK's in-memory listener
// state and resolves it. Reported as CL-1296 / Sentry CL-WEBAPP-J4 and
// upstream as firebase/firebase-js-sdk#5549.
function isFirestoreTargetIdError(error) {
  return (
    error?.name === 'FirebaseError' &&
    typeof error?.message === 'string' &&
    error.message.startsWith('Target ID already exists')
  )
}

// firebase-js-sdk @firebase/firestore occasionally serializes the first
// cold-load Listen target with a missing path segment; the server rejects
// with `[invalid-argument]: incomplete key.`. A full reload gives the SDK
// a clean session — the second attempt typically succeeds. Same recovery
// pattern as `Target ID already exists` above. CL-1319 / CL-WEBAPP-GH.
function isFirestoreIncompleteKeyError(error) {
  return (
    error?.name === 'FirebaseError' &&
    typeof error?.message === 'string' &&
    error.message.startsWith('incomplete key')
  )
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      prevState.hasError &&
      prevProps.location?.pathname !== this.props.location?.pathname
    ) {
      this.setState({ hasError: false, error: null, errorInfo: null })
    }
  }

  componentDidCatch(error, errorInfo) {
    if (isFirestoreTargetIdError(error)) {
      Sentry.captureException(error, {
        tags: { autoReload: 'firestore-target-id' },
      })

      const RELOAD_KEY = 'firestore-target-id-reload'
      const lastReload = sessionStorage.getItem(RELOAD_KEY)
      const now = Date.now()

      if (!lastReload || now - Number(lastReload) > 10_000) {
        sessionStorage.setItem(RELOAD_KEY, String(now))
        window.location.reload()
        return
      }

      this.setState({ hasError: true, error, errorInfo })
      return
    }

    if (isFirestoreIncompleteKeyError(error)) {
      Sentry.captureException(error, {
        tags: { autoReload: 'firestore-incomplete-key' },
      })

      const RELOAD_KEY = 'firestore-incomplete-key-reload'
      const lastReload = sessionStorage.getItem(RELOAD_KEY)
      const now = Date.now()

      if (!lastReload || now - Number(lastReload) > 10_000) {
        sessionStorage.setItem(RELOAD_KEY, String(now))
        window.location.reload()
        return
      }

      this.setState({ hasError: true, error, errorInfo })
      return
    }

    if (error.name === 'FirebaseError' && error.code === 'permission-denied') {
      Sentry.captureException(error, {
        tags: { errorBoundary: 'permission-denied' },
      })
    } else {
      Sentry.captureException(error)
    }
    this.setState({ hasError: true, error, errorInfo })
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorContent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary

export function ErrorContent({ error, errorInfo, auth }) {
  const { t } = useTranslation()
  // Development
  const componentStack = errorInfo?.componentStack || null
  const errorMessage =
    typeof error.message === 'undefined'
      ? (error || '').toString()
      : error.message
  const errorDescription =
    typeof error.description === 'undefined'
      ? componentStack
      : error.description

  return import.meta.env.MODE !== 'development' ? (
    <Space direction="vertical" size="large">
      <div>
        <Typography.Title level={3}>
          {t('frontend:errorBoundary.title')}
        </Typography.Title>
        <Typography.Title level={5}>
          {t('frontend:errorBoundary.message')}
        </Typography.Title>
      </div>
      <Button
        type="primary"
        size="large"
        onClick={() => window.location.reload(true)}
      >
        {t('frontend:common.reloadPage')}
      </Button>
      <ImpersonationExitButton auth={auth} />
    </Space>
  ) : (
    <Alert
      type="error"
      message={errorMessage}
      description={
        <pre style={{ fontSize: '0.9em', overflowX: 'auto' }}>
          {errorDescription}
        </pre>
      }
    />
  )
}
